import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swords, MapPin, Skull, Flag, Share2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { MiniMap } from "@/components/MiniMap";
import { ShareCardModal } from "@/components/ShareCardModal";
import { warPhrase } from "@/components/Battles";

const eraSlug = (e) => (e || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const cardFor = (b, dateLabel) => ({
  badge: "Battle of the Day", greeting: `Fought ${dateLabel}, ${b.date_label || b.year}`, title: b.battle,
  body: [b.location ? `At ${b.location}` : "", b.war ? `Part of ${warPhrase(b.war)}` : "", b.belligerents, b.result, b.casualties ? `Casualties: ${b.casualties}` : "", b.notable].filter(Boolean).map((p) => p.replace(/\.$/, "")).join(". ") + ".",
  dateLabel: b.date_label || String(b.year), footer: "Every day in history → oddlytrue.co/battles",
});

/** Homepage hero for the day's most notable battle: big vintage map, the facts, and a shareable card. */
export const BattleOfTheDay = ({ ddmm, dateLabel = "today" }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!ddmm) return;
    api.get("/battles/featured", { params: { date: ddmm } }).then((r) => setData(r.data)).catch(() => setData({ battle: null }));
  }, [ddmm]);
  const b = data?.battle;
  if (!b) return null;
  const card = cardFor(b, dateLabel);
  return (
    <article data-testid="battle-of-the-day" className="mb-6 overflow-hidden rounded-2xl border-2 border-ink bg-[#fbf8f1]">
      <div className="grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <div className="flex items-center p-3 md:p-4 bg-[#efe6d2]">
          <div className="w-full"><MiniMap lat={b.lat} lon={b.lon} zoom={6} width={640} height={420} label={b.location || b.region} href={b.map_link} testid="botd-map" /></div>
        </div>
        <div className="p-5 md:p-6 flex flex-col">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold inline-flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> Battle of the Day · {dateLabel}</p>
          <h3 data-testid="botd-title" className="mt-2 text-2xl sm:text-3xl font-bold text-ink leading-tight">{b.battle}</h3>
          <p className="mt-1 text-[13px] text-ink-muted">{b.date_label || b.year}{b.era ? <> · <Link to={`/battles/${eraSlug(b.era)}`} data-testid="botd-era-link" className="font-semibold text-gold hover:underline">{b.era}</Link></> : ""}{b.war ? ` · ${b.war}` : ""}</p>
          <div className="mt-4 space-y-1.5 text-[14px] text-ink">
            {b.location && <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-ink-muted shrink-0" /> {b.location}</p>}
            {b.belligerents && <p className="flex items-start gap-2"><Flag className="w-4 h-4 mt-0.5 text-ink-muted shrink-0" /> {b.belligerents}</p>}
            {b.result && <p className="text-ink-muted">{b.result}</p>}
            {b.casualties && <p className="flex items-start gap-2 text-ink-muted"><Skull className="w-4 h-4 mt-0.5 shrink-0" /> {b.casualties}</p>}
            {b.notable && <p className="italic text-ink-muted border-t border-ink/10 pt-2">{b.notable}</p>}
          </div>
          <div className="mt-auto pt-5 flex flex-wrap items-center gap-3">
            <button type="button" data-testid="botd-share" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-5 h-10 bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors"><Share2 className="w-4 h-4" /> Share this battle</button>
            {b.wiki_url && <a href={b.wiki_url} target="_blank" rel="noopener noreferrer" data-testid="botd-wiki" className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink-muted hover:text-gold"><ExternalLink className="w-3.5 h-3.5" /> Wikipedia</a>}
            {data.others > 0 && <span data-testid="botd-others" className="text-[12px] text-ink-muted">+{data.others} more fought {dateLabel}</span>}
          </div>
        </div>
      </div>
      <ShareCardModal open={open} onClose={() => setOpen(false)} card={card} shareUrl={`${window.location.origin}/battles`} heading="Share the Battle of the Day" analyticsName="botd_share" testPrefix="botd-card"
        shareText={`${b.battle} (${b.date_label || b.year}) — today's Battle of the Day on OddlyTrue`} />
    </article>
  );
};
